/**
 * ForderungsmanagerDrawer — Betriebs-übergreifender Forderungsmanager (Sheet von rechts)
 *
 * Issue #75: Drawer öffnet sich aus der Sidebar und zeigt alle Forderungen.
 * Issue #600 (B3): 80vw-Breite, 40/60-Split-Layout, vollständige Filter-Bar
 *   (Stage, Typ, Versicherung, Betrag, Alter), localStorage-Persistierung.
 * Issue #601 (B4): Virtualisierte Liste, Multi-Select, Sticky-Footer,
 *   Detail-Tab Übersicht + Cross-Link-Card.
 */

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Slider } from '@/components/ui/slider';
import {
  Scale,
  Euro,
  AlertTriangle,
  FileText,
  Settings2,
  ChevronDown,
  RotateCcw,
  ChevronsUpDown,
  Check,
  Car,
  Wrench,
  TrendingUp,
  Send,
  MessageSquare,
  Building2,
  Shield,
} from 'lucide-react';
import { useRechnungen } from '@/hooks/useSupabase';
import { useForderungen, type Forderungsakte } from '@/hooks/useForderungen';
import { useMarken } from '@/hooks/useMarken';
import { useForderungenCrossLinks } from '@/hooks/useForderungenCrossLinks';
import { CrossLinkChips } from '@/components/forderungsmanager/CrossLinkChips';
import { MassenAnschreibenModal } from '@/components/forderungsmanager/MassenAnschreibenModal';
import { ForderungStagePill } from '@/components/forderung/ForderungStagePill';
import { AnschreibenTab } from '@/components/forderungsmanager/AnschreibenTab';
import { TimelineTab } from '@/components/forderungsmanager/TimelineTab';
import { NotizenTab } from '@/components/forderungsmanager/NotizenTab';
import { ZahlungseingaengeTab } from '@/components/forderungsmanager/ZahlungseingaengeTab';
import { deriveCrossLinks } from '@/lib/cross-link-chips';
import {
  toggleSelection,
  clearSelection,
  isSelected,
  selectionCount,
  shouldShowStickyFooter,
  computeStageNext,
  mapMahnstufeToStage,
  mapStageToMahnstufe,
  formatTageSeitFaelligkeit,
  buildDetailHeaderFields,
} from '@/lib/forderungsmanager-master-detail';
import {
  applyForderungenFilter,
  distinctVersicherungen,
  DEFAULT_FILTER_STATE,
  BETRAG_SLIDER_MAX,
  ALTERS_BUCKET_DEFS,
  AKTEN_TYP_FILTER_LIST,
  LOCALSTORAGE_KEY,
  type ForderungenFilterState,
  type AltersBucketKey,
  type AktenTypId,
} from '@/lib/forderungenFilter';
import { supabase, type BusinessUnit } from '@/lib/supabase';

// ─── Konstanten ────────────────────────────────────────────
export const DEFAULT_MAHNSTUFEN: Array<{ stufe: string; label: string; tage: number }> = [
  { stufe: 'zahlungserinnerung', label: 'Zahlungserinnerung', tage: 14 },
  { stufe: '1_mahnung', label: '1. Mahnung', tage: 30 },
  { stufe: '2_mahnung', label: '2. Mahnung', tage: 45 },
  { stufe: 'inkasso', label: 'Inkasso-Übergabe', tage: 60 },
];

// Stage-Farb-Klassen inline (bis ForderungStagePill vollständig integriert)
const STAGE_CLASSES: Record<number, string> = {
  0: 'bg-gray-100 text-gray-600',
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-yellow-100 text-yellow-700',
  4: 'bg-orange-100 text-orange-700',
  5: 'bg-red-100 text-red-700',
  6: 'bg-red-100 text-red-700',
  7: 'bg-red-100 text-red-800',
  8: 'bg-red-200 text-red-900',
  9: 'bg-green-50 text-zinc-500',
};

const AKTEN_TYP_ICONS: Record<string, typeof Car> = {
  mietwagen: Car,
  werkstatt: Wrench,
  autoglas: Shield,
};

const ITEM_HEIGHT = 96;

// ─── Hilfsfunktionen ───────────────────────────────────────
function formatCurrency(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE');
}

function isOverdue(r: { status: string; faellig_am: string | null }): boolean {
  if (r.status === 'bezahlt') return false;
  if (!r.faellig_am) return false;
  return new Date(r.faellig_am).getTime() < Date.now();
}

function restbetrag(r: { brutto_betrag: number; bezahlt_betrag: number; kuerzung_betrag: number }): number {
  return Math.max(0, (r.brutto_betrag || 0) - (r.bezahlt_betrag || 0) - (r.kuerzung_betrag || 0));
}

function loadFilterFromStorage(): ForderungenFilterState {
  try {
    const raw = localStorage.getItem(LOCALSTORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      return {
        ...DEFAULT_FILTER_STATE,
        ...saved,
        // JSON.stringify(Infinity) → null; treat null as "no upper limit"
        betragsMax: saved.betragsMax ?? Infinity,
      };
    }
  } catch {
    // ignore
  }
  return DEFAULT_FILTER_STATE;
}

// ─── Props ─────────────────────────────────────────────────
interface ForderungsmanagerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  akteId?: string;
}

// ═══════════════════════════════════════════════════════════
export default function ForderungsmanagerDrawer({ open, onOpenChange, akteId }: ForderungsmanagerDrawerProps) {
  // ── Rechnungen (Kennzahlen, Betrieb-Filter) ───────────────
  const [filterBetrieb, setFilterBetrieb] = useState<BusinessUnit | 'alle'>('alle');
  const [mahnstufen, setMahnstufen] = useState(() => {
    try {
      const saved = localStorage.getItem('forderungsmanager_mahnstufen');
      if (saved) return JSON.parse(saved) as typeof DEFAULT_MAHNSTUFEN;
    } catch {
      // fall through
    }
    return DEFAULT_MAHNSTUFEN;
  });
  const [mahnstufenOpen, setMahnstufenOpen] = useState(false);

  // ── Multi-Select (B4) ────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [massenAnschreibenOpen, setMassenAnschreibenOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Resolved forderungsakte IDs for the given akteId, via the view's COALESCE(fa.akte_id, r.akte_id).
  // Needed because forderungsakten.akte_id is NULL for existing rows until backfill (#275).
  const [resolvedForderungsIds, setResolvedForderungsIds] = useState<string[] | null>(null);
  const [akteFilterError, setAkteFilterError] = useState<string | null>(null);

  useEffect(() => {
    if (!akteId) {
      setResolvedForderungsIds(null);
      setAkteFilterError(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('forderungsakten_vollstaendig')
      .select('forderungsakte_id')
      .eq('akte_id', akteId)
      .then(({ data, error }) => {
        if (!cancelled) {
          if (error) {
            setAkteFilterError(error.message);
            setResolvedForderungsIds([]);
          } else {
            setAkteFilterError(null);
            setResolvedForderungsIds((data ?? []).map((r) => r.forderungsakte_id as string));
          }
        }
      });
    return () => { cancelled = true; };
  }, [akteId]);

  useEffect(() => {
    try {
      localStorage.setItem('forderungsmanager_mahnstufen', JSON.stringify(mahnstufen));
    } catch {
      // localStorage may be unavailable
    }
  }, [mahnstufen]);

  const { data: rechnungen } = useRechnungen(
    filterBetrieb === 'alle' ? undefined : (filterBetrieb as BusinessUnit),
  );

  // ── Forderungen (Filter-Bar, 40/60-Split, Virtualizer) ───
  const { forderungen, loading: forderungenLoading, updateForderung } = useForderungen();
  const { crossLinks } = useForderungenCrossLinks(forderungen);

  const forderungenByAkte = useMemo(() => {
    if (!akteId) return forderungen;
    if (resolvedForderungsIds === null) return [];
    return forderungen.filter((f) => resolvedForderungsIds.includes(f.id));
  }, [forderungen, akteId, resolvedForderungsIds]);

  const stats = useMemo(() => {
    if (akteId) {
      // Akte-specific: use forderungen filtered to this akte
      const list = forderungenByAkte;
      const offen = list.filter((f) => f.status !== 'erledigt');
      const ueberfaellig = offen.filter(
        (f) => f.faellig_am && new Date(f.faellig_am) < new Date(),
      );
      return {
        offeneSumme: offen.reduce((s, f) => s + (f.gesamtbetrag || 0), 0),
        ueberfaelligSumme: ueberfaellig.reduce((s, f) => s + (f.gesamtbetrag || 0), 0),
        anzahlOffen: offen.length,
        anzahlUeberfaellig: ueberfaellig.length,
      };
    }
    const list = rechnungen || [];
    const offen = list.filter((r) => ['offen', 'gesendet', 'ueberfaellig'].includes(r.status));
    const ueberfaellig = list.filter(isOverdue);
    return {
      offeneSumme: offen.reduce((s, r) => s + restbetrag(r), 0),
      ueberfaelligSumme: ueberfaellig.reduce((s, r) => s + restbetrag(r), 0),
      anzahlOffen: offen.length,
      anzahlUeberfaellig: ueberfaellig.length,
    };
  }, [akteId, forderungenByAkte, rechnungen]);

  const [filter, setFilter] = useState<ForderungenFilterState>(loadFilterFromStorage);
  const [versicherungOpen, setVersicherungOpen] = useState(false);
  const [selectedMarke, setSelectedMarke] = useState<string | null>(null);
  const { data: marken } = useMarken();

  useEffect(() => {
    if (akteId) return;
    try {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(filter));
    } catch {
      // ignore
    }
  }, [filter, akteId]);

  const versicherungsListe = useMemo(
    () => distinctVersicherungen(akteId ? forderungenByAkte : forderungen),
    [forderungen, forderungenByAkte, akteId],
  );

  const gefilterteForderungen = useMemo(() => {
    const byFilter = applyForderungenFilter(forderungenByAkte, filter);
    if (!selectedMarke) return byFilter;
    const marke = marken.find((m) => m.marken_name === selectedMarke && m.ist_aktiv);
    if (!marke) return byFilter;
    const bu = marke.business_unit;
    if (bu === 'mietwagen') return byFilter.filter((f) => f.typ === 'mietfahrzeug');
    if (bu === 'werkstatt') return byFilter.filter((f) => f.typ === 'werkstatt_auslage');
    if (bu === 'autoglas') return byFilter.filter((f) => f.typ === 'autoglas');
    return byFilter;
  }, [forderungenByAkte, filter, selectedMarke, marken]);

  // Auto-select first item only on the initial open for a given akteId,
  // not on every forderungen refresh (e.g. after updateForderung calls).
  const autoSelectedForRef = useRef<string | undefined>(undefined);
  // Tracks previous akteId to distinguish entering vs. exiting akte-mode.
  const prevAkteIdRef = useRef<string | undefined>(undefined);

  // Reset selection state when akteId changes (including to undefined)
  useEffect(() => {
    const prevAkteId = prevAkteIdRef.current;
    prevAkteIdRef.current = akteId;
    autoSelectedForRef.current = undefined;
    setActiveId(null);
    setSelectedIds(new Set());
    setSelectedMarke(null);
    if (akteId) {
      // Entering akte-filtered mode: start with a clean filter.
      setFilter(DEFAULT_FILTER_STATE);
    } else if (prevAkteId !== undefined) {
      // Exiting akte-filtered mode: restore the user's persisted filter so the
      // close transition doesn't overwrite their global filter in localStorage.
      setFilter(loadFilterFromStorage());
    }
  }, [akteId]);

  useEffect(() => {
    if (!akteId || forderungenLoading || resolvedForderungsIds === null) return;
    if (autoSelectedForRef.current === akteId) return;
    autoSelectedForRef.current = akteId;
    const first = forderungenByAkte[0];
    if (first) setActiveId(first.id);
  }, [akteId, forderungenByAkte, forderungenLoading, resolvedForderungsIds]);

  // ─── Virtualizer ─────────────────────────────────────────
  const virtualizer = useVirtualizer({
    count: gefilterteForderungen.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
  });

  const activeItem = useMemo(
    () => (activeId ? forderungen.find((f) => f.id === activeId) ?? null : null),
    [activeId, forderungen],
  );

  // ─── Multi-Select Aktionen ────────────────────────────────
  const handleCheckboxChange = useCallback((id: string) => {
    setSelectedIds((prev) => toggleSelection(prev, id));
  }, []);

  const handleMassenStageUp = useCallback(async () => {
    const selected = Array.from(selectedIds);
    await Promise.all(
      selected.map((id) => {
        const f = forderungen.find((x) => x.id === id);
        if (!f) return Promise.resolve();
        const currentStage = f.stage ?? mapMahnstufeToStage(f.mahnstufe);
        const nextStage = computeStageNext(currentStage);
        return updateForderung(id, { mahnstufe: mapStageToMahnstufe(nextStage) });
      }),
    );
    setSelectedIds(clearSelection(selectedIds));
  }, [selectedIds, forderungen, updateForderung]);

  const updateMahnstufeTage = (stufe: string, tage: number) => {
    setMahnstufen((prev) => prev.map((m) => (m.stufe === stufe ? { ...m, tage } : m)));
  };

  const toggleStage = (stage: number) => {
    setFilter((prev) => ({
      ...prev,
      stages: prev.stages.includes(stage)
        ? prev.stages.filter((s) => s !== stage)
        : [...prev.stages, stage],
    }));
  };

  const toggleAktenTyp = (id: AktenTypId) => {
    setFilter((prev) => ({
      ...prev,
      aktenTypen: prev.aktenTypen.includes(id)
        ? prev.aktenTypen.filter((t) => t !== id)
        : [...prev.aktenTypen, id],
    }));
  };

  const toggleAltersBucket = (key: AltersBucketKey) => {
    setFilter((prev) => ({
      ...prev,
      altersBuckets: prev.altersBuckets.includes(key)
        ? prev.altersBuckets.filter((b) => b !== key)
        : [...prev.altersBuckets, key],
    }));
  };

  const resetFilter = () => setFilter(DEFAULT_FILTER_STATE);

  const isFilterActive =
    filter.stages.length > 0 ||
    filter.aktenTypen.length > 0 ||
    filter.versicherung !== '' ||
    filter.betragsMin !== DEFAULT_FILTER_STATE.betragsMin ||
    filter.betragsMax !== DEFAULT_FILTER_STATE.betragsMax ||
    filter.altersBuckets.length > 0;

  return (
    <TooltipProvider>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[80vw] p-0 flex flex-col gap-0"
        >
          <SheetHeader className="border-b px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <SheetTitle className="flex items-center gap-2 text-xl">
                <Scale className="w-6 h-6 text-amber-600" />
                Forderungsmanager
              </SheetTitle>
              {/* Brand-Switcher (Issue #282) — filtert Forderungen clientseitig nach Marke */}
              {marken.filter((m) => m.ist_aktiv).length > 0 && (
                <div className="flex items-center gap-1.5 flex-shrink-0" data-testid="brand-switcher">
                  <button
                    type="button"
                    onClick={() => setSelectedMarke(null)}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                      selectedMarke === null
                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                        : 'bg-transparent text-muted-foreground border-muted-foreground/30 hover:border-muted-foreground'
                    }`}
                  >
                    Alle
                  </button>
                  {marken
                    .filter((m) => m.ist_aktiv)
                    .map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSelectedMarke(selectedMarke === m.marken_name ? null : m.marken_name)}
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                          selectedMarke === m.marken_name
                            ? 'bg-sky-100 text-sky-800 border-sky-300'
                            : 'bg-transparent text-muted-foreground border-muted-foreground/30 hover:border-muted-foreground'
                        }`}
                        data-testid={`brand-chip-${m.marken_name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {m.marken_name}
                      </button>
                    ))}
                </div>
              )}
            </div>
            <SheetDescription>
              Forderungsakten über alle Betriebe — Filter, Mahnlauf-Steuerung.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Kennzahlen */}
            <div className="grid grid-cols-2 gap-3 px-6 py-4 border-b flex-shrink-0">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <Euro className="w-3.5 h-3.5" />
                    {akteId ? 'Offene Forderungen' : 'Offene Rechnungen'}
                  </div>
                  <div className="text-lg font-semibold">{formatCurrency(stats.offeneSumme)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stats.anzahlOffen} offen
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                    Überfällig
                  </div>
                  <div className="text-lg font-semibold text-red-700">
                    {formatCurrency(stats.ueberfaelligSumme)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stats.anzahlUeberfaellig} überfällig
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Error banner when akte-scoped lookup fails */}
            {akteId && akteFilterError && (
              <div className="px-6 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2 text-xs text-red-700 flex-shrink-0">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Forderungen konnten nicht geladen werden: {akteFilterError}
              </div>
            )}

            {/* ── Filter-Bar (über beide Spalten) ── */}
            <div className="px-6 py-3 border-b bg-muted/20 flex-shrink-0 space-y-3">
              {/* Zeile 1: Stage + Akten-Typ + Reset */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Stage Multi-Select (0–9) */}
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: 10 }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleStage(i)}
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                        filter.stages.includes(i)
                          ? (STAGE_CLASSES[i] ?? 'bg-gray-100 text-gray-600') + ' border-current'
                          : 'bg-transparent text-muted-foreground border-muted-foreground/30 hover:border-muted-foreground'
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>

                <div className="h-4 w-px bg-border" />

                {/* Akten-Typ Chips */}
                <div className="flex flex-wrap gap-1">
                  {AKTEN_TYP_FILTER_LIST.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleAktenTyp(t.id as AktenTypId)}
                      className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                        filter.aktenTypen.includes(t.id as AktenTypId)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-transparent text-muted-foreground border-muted-foreground/30 hover:border-muted-foreground'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {forderungenLoading ? '…' : `${gefilterteForderungen.length} Forderungen`}
                  </span>
                  {isFilterActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetFilter}
                      className="h-7 gap-1 text-xs"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>

              {/* Zeile 2: Versicherung + Betrag + Alters-Buckets */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Versicherung Combobox */}
                <Popover open={versicherungOpen} onOpenChange={setVersicherungOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={versicherungOpen}
                      className="h-8 min-w-[180px] justify-between text-xs font-normal"
                    >
                      {filter.versicherung || 'Versicherung…'}
                      <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[240px] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Versicherung suchen…"
                        value={filter.versicherung}
                        onValueChange={(v) => setFilter((prev) => ({ ...prev, versicherung: v }))}
                      />
                      <CommandList>
                        <CommandEmpty>Keine Versicherung gefunden.</CommandEmpty>
                        <CommandGroup>
                          {filter.versicherung && !versicherungsListe.includes(filter.versicherung) && (
                            <CommandItem
                              value=""
                              onSelect={() => {
                                setFilter((prev) => ({ ...prev, versicherung: '' }));
                                setVersicherungOpen(false);
                              }}
                            >
                              Alle anzeigen
                            </CommandItem>
                          )}
                          {versicherungsListe.map((v) => (
                            <CommandItem
                              key={v}
                              value={v}
                              onSelect={(val) => {
                                setFilter((prev) => ({
                                  ...prev,
                                  versicherung: prev.versicherung === val ? '' : val,
                                }));
                                setVersicherungOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-3 w-3 ${filter.versicherung === v ? 'opacity-100' : 'opacity-0'}`}
                              />
                              {v}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Betragsbereich Slider */}
                <div className="flex items-center gap-2 min-w-[200px]">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatCurrency(filter.betragsMin)}
                  </span>
                  <Slider
                    min={0}
                    max={BETRAG_SLIDER_MAX}
                    step={100}
                    value={[filter.betragsMin, filter.betragsMax === Infinity ? BETRAG_SLIDER_MAX : filter.betragsMax]}
                    onValueChange={([min, max]) =>
                      setFilter((prev) => ({
                        ...prev,
                        betragsMin: min,
                        betragsMax: max >= BETRAG_SLIDER_MAX ? Infinity : max,
                      }))
                    }
                    className="w-32"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {filter.betragsMax === Infinity
                      ? `${formatCurrency(BETRAG_SLIDER_MAX)}+`
                      : formatCurrency(filter.betragsMax)}
                  </span>
                </div>

                {/* Alters-Buckets */}
                <div className="flex flex-wrap gap-1">
                  {ALTERS_BUCKET_DEFS.map((b) => (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => toggleAltersBucket(b.key as AltersBucketKey)}
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                        filter.altersBuckets.includes(b.key as AltersBucketKey)
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-transparent text-muted-foreground border-muted-foreground/30 hover:border-muted-foreground'
                      }`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>

                {/* Mahnstufen konfigurieren */}
                <Collapsible open={mahnstufenOpen} onOpenChange={setMahnstufenOpen} className="ml-auto">
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs">
                      <Settings2 className="w-3.5 h-3.5" />
                      Mahnstufen
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${mahnstufenOpen ? 'rotate-180' : ''}`}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="absolute z-10 mt-1 w-72 right-6 bg-white border rounded-lg shadow-lg p-4">
                    <div className="text-xs text-muted-foreground mb-3">
                      Standard-Fristen (Tage nach Fälligkeit).
                    </div>
                    {mahnstufen.map((m) => (
                      <div key={m.stufe} className="flex items-center gap-3 mb-2">
                        <div className="flex-1 text-xs">{m.label}</div>
                        <Input
                          type="number"
                          min={0}
                          max={365}
                          value={m.tage}
                          onChange={(e) => updateMahnstufeTage(m.stufe, Number(e.target.value) || 0)}
                          className="w-16 text-right h-7 text-xs"
                        />
                        <span className="text-xs text-muted-foreground">Tage</span>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>

            {/* ── Master-Detail Split: 40% Liste | 60% Detail ── */}
            <div className="flex flex-1 overflow-hidden relative">
              {/* ── Linke Spalte — Virtualisierte Liste ────────── */}
              <div className="w-full md:w-[40%] border-r flex flex-col overflow-hidden">
                {forderungenLoading && (
                  <div className="py-10 text-center text-sm text-muted-foreground">Lade…</div>
                )}
                {!forderungenLoading && gefilterteForderungen.length === 0 && (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    Keine Ergebnisse.
                  </div>
                )}

                {!forderungenLoading && gefilterteForderungen.length > 0 && (
                  <div
                    ref={listRef}
                    className="flex-1 overflow-y-auto"
                    data-testid="forderungen-list"
                  >
                    <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                      {virtualizer.getVirtualItems().map((virtualRow) => {
                        const f = gefilterteForderungen[virtualRow.index];
                        if (!f) return null;
                        const selected = isSelected(selectedIds, f.id);
                        const isActive = activeId === f.id;
                        const stage = f.stage ?? mapMahnstufeToStage(f.mahnstufe);
                        const tageSeit = formatTageSeitFaelligkeit(f.unfall_datum);
                        const AktenIcon =
                          AKTEN_TYP_ICONS[f.typ === 'werkstatt_auslage' ? 'werkstatt' : 'mietwagen'] ??
                          FileText;

                        return (
                          <div
                            key={f.id}
                            data-testid={`forderung-card-${f.id}`}
                            style={{
                              position: 'absolute',
                              top: virtualRow.start,
                              left: 0,
                              right: 0,
                              height: ITEM_HEIGHT,
                            }}
                            className={`flex items-start gap-2 px-3 py-2 border-b cursor-pointer transition-colors ${
                              isActive
                                ? 'bg-amber-50 border-l-2 border-l-amber-500'
                                : 'hover:bg-muted/40'
                            }`}
                            onClick={() => setActiveId(f.id)}
                          >
                            <div
                              className="pt-0.5 flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange(f.id);
                              }}
                            >
                              <Checkbox
                                checked={selected}
                                aria-label={`Forderung ${f.akten_nr ?? f.id} auswählen`}
                                data-testid={`checkbox-${f.id}`}
                              />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-mono text-xs font-semibold truncate">
                                  {f.akten_nr ?? '—'}
                                </span>
                                <AktenIcon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                              </div>
                              <div className="text-xs text-muted-foreground truncate mb-1">
                                {f.unfallgegner_name ?? '—'}
                              </div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs font-semibold tabular-nums">
                                  {formatCurrency(f.gesamtbetrag || 0)}
                                </span>
                                <ForderungStagePill stage={stage} forderungsAkteId={f.id} />
                                {tageSeit > 0 && (
                                  <span className="text-[10px] text-red-600">+{tageSeit}d</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sticky Footer — erscheint bei ≥ 1 Auswahl */}
                {shouldShowStickyFooter(selectedIds) && (
                  <div
                    data-testid="selection-footer"
                    className="border-t bg-amber-50 px-3 py-2 flex-shrink-0 flex flex-col gap-2"
                  >
                    <div className="text-xs font-medium text-amber-800">
                      {selectionCount(selectedIds)} ausgewählt
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1"
                            data-testid="btn-massen-stage"
                            onClick={handleMassenStageUp}
                          >
                            <TrendingUp className="w-3 h-3" />
                            Stage +1
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Stage für alle ausgewählten Forderungen erhöhen</TooltipContent>
                      </Tooltip>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        data-testid="btn-anschreiben"
                        onClick={() => setMassenAnschreibenOpen(true)}
                      >
                        <Send className="w-3 h-3" />
                        Anschreiben
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        data-testid="btn-inkasso"
                        onClick={() => {
                          /* TODO stub — Inkasso */
                        }}
                      >
                        <Building2 className="w-3 h-3" />
                        Inkasso
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        data-testid="btn-notiz"
                        onClick={() => {
                          /* TODO stub — Notiz an alle */
                        }}
                      >
                        <MessageSquare className="w-3 h-3" />
                        Notiz
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Rechte Spalte — Detail-Panel ──────────────── */}
              <div
                className="hidden md:flex md:w-[60%] overflow-y-auto"
                data-testid="forderung-detail-panel"
              >
                {!activeItem ? (
                  <div className="flex flex-col items-center justify-center h-full w-full text-sm text-muted-foreground gap-2 p-8 text-center">
                    <Scale className="w-10 h-10 opacity-20" />
                    <p>Forderung in der Liste auswählen, um Details anzuzeigen.</p>
                  </div>
                ) : (
                  <DetailPanel
                    forderung={activeItem}
                    crossLinkState={crossLinks[activeItem.id] ?? { G: null, M: null, W: null }}
                    onStageChange={(newStage) =>
                      updateForderung(activeItem.id, { stage: newStage })
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <MassenAnschreibenModal
        open={massenAnschreibenOpen}
        onOpenChange={setMassenAnschreibenOpen}
        selectedForderungen={gefilterteForderungen.filter(f => selectedIds.has(f.id))}
        onComplete={() => {
          setSelectedIds(clearSelection(selectedIds));
        }}
      />
    </TooltipProvider>
  );
}

// ─── Detail-Panel ─────────────────────────────────────────
interface DetailPanelProps {
  forderung: Forderungsakte;
  crossLinkState: ReturnType<typeof deriveCrossLinks>;
  onStageChange?: (newStage: number) => void;
}

function DetailPanel({ forderung, crossLinkState, onStageChange }: DetailPanelProps) {
  const header = buildDetailHeaderFields(forderung);
  const stage = forderung.stage ?? mapMahnstufeToStage(forderung.mahnstufe);

  return (
    <div className="p-4 space-y-4 w-full" data-testid="detail-panel-content">
      {/* Header-Block */}
      <Card className="shadow-none">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="font-mono text-sm font-bold">{header.aktenzeichen}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{header.schuldner}</div>
            </div>
            <ForderungStagePill stage={stage} forderungsAkteId={forderung.id} />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <div>
              <span className="text-muted-foreground">Brutto: </span>
              <span className="font-semibold">{formatCurrency(forderung.gesamtbetrag || 0)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Offen: </span>
              <span className="font-semibold tabular-nums" data-testid="detail-betrag-offen">
                {formatCurrency(header.betragOffen)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Fällig: </span>
              <span>{formatDate(header.faelligkeit)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Verjährung: </span>
              <span data-testid="detail-verjaehrung">{formatDate(header.verjaehrung)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs — Übersicht, Anschreiben, Timeline, Notizen, Zahlungseingänge */}
      <Tabs defaultValue="uebersicht">
        <TabsList className="w-full grid grid-cols-5">
          <TabsTrigger value="uebersicht" className="text-xs px-1">
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="anschreiben" className="text-xs px-1">
            Anschreiben
          </TabsTrigger>
          <TabsTrigger value="timeline" className="text-xs px-1">
            Timeline
          </TabsTrigger>
          <TabsTrigger value="notizen" className="text-xs px-1">
            Notizen
          </TabsTrigger>
          <TabsTrigger value="zahlungen" className="text-xs px-1">
            Zahlung
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Übersicht ── */}
        <TabsContent value="uebersicht" className="space-y-3 mt-3">
          {/* Cross-Link-Card */}
          <Card className="shadow-none">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Verknüpfungen
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4">
              <CrossLinkChips state={crossLinkState} data-testid="cross-link-chips" />
              <p className="text-xs text-muted-foreground mt-2">
                Gutachten ↔ Mietwagen ↔ Werkstatt
              </p>
            </CardContent>
          </Card>

          {/* Unfalldetails */}
          <Card className="shadow-none">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Unfall
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <DetailRow label="Datum" value={formatDate(forderung.unfall_datum)} />
              <DetailRow label="Ort" value={forderung.unfall_ort} />
              <DetailRow label="Gegner Kfz" value={forderung.unfallgegner_kennzeichen} />
              <DetailRow label="Schadennr." value={forderung.schaden_nummer} />
            </CardContent>
          </Card>

          {/* Versicherung */}
          <Card className="shadow-none">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Versicherung
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <DetailRow label="Name" value={forderung.versicherung_gegner} />
              <DetailRow label="Vers.-Nr." value={forderung.versicherungsnummer} />
              <DetailRow label="E-Mail" value={forderung.versicherung_email} />
              <DetailRow label="Telefon" value={forderung.versicherung_telefon} />
            </CardContent>
          </Card>

          {/* Mietvertrag */}
          <Card className="shadow-none">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Mietvertrag
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <DetailRow label="Mietvertrag-Nr." value={forderung.mietvertrag_nr} />
              <DetailRow label="Mietbeginn" value={formatDate(forderung.mietbeginn)} />
              <DetailRow label="Mietende" value={formatDate(forderung.mietende)} />
              <DetailRow
                label="Mietdauer"
                value={forderung.mietdauer_tage ? `${forderung.mietdauer_tage} Tage` : null}
              />
              <DetailRow
                label="Tagessatz"
                value={forderung.tagessatz ? formatCurrency(forderung.tagessatz) : null}
              />
              <DetailRow
                label="Gesamtbetrag"
                value={formatCurrency(forderung.gesamtbetrag || 0)}
              />
            </CardContent>
          </Card>

          {/* Abtretung */}
          {forderung.abtretung_status && (
            <Card className="shadow-none">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Abtretung
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-3 px-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <DetailRow label="Status" value={forderung.abtretung_status} />
                <DetailRow label="Datum" value={formatDate(forderung.abtretung_datum)} />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab: Anschreiben ── */}
        <TabsContent value="anschreiben" className="mt-3">
          <AnschreibenTab
            forderungsAkteId={forderung.id}
            stage={stage}
            versicherungEmail={forderung.versicherung_email}
            versicherungGegner={forderung.versicherung_gegner}
            schadenNummer={forderung.schaden_nummer}
            rechnungsNr={forderung.rechnung_nr}
            betragOffen={header.betragOffen}
            onStageChange={onStageChange}
          />
        </TabsContent>

        {/* ── Tab: Timeline ── */}
        <TabsContent value="timeline" className="mt-3">
          <TimelineTab forderungsAkteId={forderung.id} />
        </TabsContent>

        {/* ── Tab: Notizen ── */}
        <TabsContent value="notizen" className="mt-3">
          <NotizenTab forderungsAkteId={forderung.id} />
        </TabsContent>

        {/* ── Tab: Zahlungseingänge ── */}
        <TabsContent value="zahlungen" className="mt-3">
          <ZahlungseingaengeTab
            forderungsAkteId={forderung.id}
            rechnungsId={forderung.rechnungs_id ?? null}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium truncate">{value || '—'}</span>
    </>
  );
}
